import React, {
  useState,
  Children,
  isValidElement,
  useLayoutEffect,
  useMemo,
} from 'react'
import { TabProps, Tab } from './Tab'
import { TabHeader } from './components'
import { SliderContent } from '../Animations/Slider/SliderContent'
import { navigateToSlideIndex } from '../Animations/Slider/slider.helpers'
import { twMerge } from 'tailwind-merge'
import { useSearchParamsWithoutRequiringReactRouter } from '../../hooks/useSearchParamsWithoutRequiringReactRouter'

type BaseProps = {
  className?: string
  tabHeaderClasses?: string
  children: (React.FunctionComponentElement<TabProps> | React.ReactNode)[] // Children Should have step props
  fullPage?: boolean
  routing?: boolean
  disableRouting?: boolean
  tabTitleClasses?: string
}
type UncontrolledProps = BaseProps & {
  defaultTab?: number
  tab?: never
  setTab?: never
}
type ControlledProps = BaseProps & {
  defaultTab?: never
  tab: number
  setTab: (tab: number) => void
}

export const Tabs = (props: UncontrolledProps | ControlledProps) => {
  const internalState = useState((props.defaultTab || 1) - 1)
  // setTab discriminates; a controlled tab may be 0
  const [currentTabIndex, setCurrentTabIndex] = props.setTab
    ? ([props.tab, props.setTab] as const)
    : internalState

  const [queryParams, setQueryParams] =
    useSearchParamsWithoutRequiringReactRouter()

  const stepPropsList = useMemo(
    () =>
      Children.toArray(props.children)
        .filter(isValidElement<TabProps>)
        .map((child) => child.props),
    [props.children],
  )

  const tabParam = queryParams.get('tab')
  const urlTabIndex = stepPropsList.findIndex((prop) => prop.title === tabParam)

  // Only a ?tab= or tab-list change syncs; clicks win
  useLayoutEffect(() => {
    if (urlTabIndex === -1 || urlTabIndex === currentTabIndex) return
    setCurrentTabIndex(urlTabIndex)
  }, [urlTabIndex])

  return (
    <section
      className={twMerge(
        `w-full box-border flex flex-col justify-between items-stretch grow overflow-clip`,
        props.className,
      )}>
      <TabHeader
        routing={props.routing}
        tabTitleClasses={props.tabTitleClasses}
        className={props.tabHeaderClasses}
        currentStep={currentTabIndex}
        stepPropsList={stepPropsList}
        onClickTitle={(tabIndex) => {
          const nextIndex = navigateToSlideIndex(
            tabIndex,
            currentTabIndex,
            stepPropsList,
          )
          setCurrentTabIndex(nextIndex)
          // Refused navigation must not reach the URL
          if (!props.disableRouting && nextIndex === tabIndex) {
            setQueryParams(
              (prev) =>
                new URLSearchParams([
                  ...[...prev].filter(([key]) => key !== 'tab'),
                  ['tab', stepPropsList[tabIndex].title],
                ]),
            )
          }
          stepPropsList[tabIndex].onClick?.()
        }}
      />

      <SliderContent
        selectedSlide={currentTabIndex}
        className={
          stepPropsList[currentTabIndex]?.className
            ? stepPropsList[currentTabIndex].className
            : undefined
        }>
        {props.children}
      </SliderContent>
    </section>
  )
}

Tabs.Tab = Tab
